// Extracts a human-readable message from whatever an API failure arrives as, falling back to
// a generic message only when nothing better is available.
//
// It has to cope with several shapes, because the error changes form as it travels:
//   * an axios error            — the raw rejection inside a thunk, message in err.response.data
//   * a plain string            — what .unwrap() throws after rejectWithValue(getApiErrorMessage(...))
//   * a SerializedError         — what .unwrap() throws for a thunk WITHOUT rejectWithValue;
//                                 redux has already stripped err.response by then, so the API's
//                                 message is gone and only the axios text ("Request failed with
//                                 status code 403") survives. That is why write thunks must use
//                                 rejectWithValue — see the slices.
//   * an Error                  — anything thrown client-side
//
// The API's own bodies: controllers return BadRequest("plain string") for validation, and
// RequirePermissionAttribute returns { error: "Forbidden", message: "Your role does not grant
// the 'Purchases:Create' permission." } for a 403 — the message a user actually needs to see.
export const getApiErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err || fallback;

  const d = err?.response?.data;
  const fromBody =
    d?.message ||
    d?.title ||
    (typeof d === "string" ? d : null) ||
    // ASP.NET model-validation problem details: { errors: { field: ["msg", ...] } }
    (d?.errors && typeof d.errors === "object"
      ? Object.values(d.errors).flat().filter(Boolean)[0]
      : null);
  if (fromBody) return fromBody;

  // No response body: distinguish "server said no" from "never reached the server", both of
  // which otherwise surface as an identical generic failure.
  const status = err?.response?.status;
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (err?.response === undefined && (err?.request || err?.code === "ERR_NETWORK")) {
    return "Could not reach the server. Check your connection and try again.";
  }

  return fallback;
};
